/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { httpService } from 'src/core/services/api/httpService';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { ConversationPublishSlotStore } from 'src/luis/core/stores/ConversationPublishSlotStore';
import {
    publishMetaDataSwaggerToPublishSlotMetaDataStore,
    publishSlotMetaDataStoreToPublishMetaDataSwagger
} from 'src/luis/projects/publishPane/services/mappers';
import { PublishSlotMetadataSwagger } from 'src/luis/projects/publishPane/types/swagger/PublishSlotMetadataSwagger';

/**
 * Represents a service for manipulation of app publish settings.
 */
export const getPublishService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Service to publish an application.
     *
     * @param param0.isStaging Flag to determines if the publish app for staging or production environment.
     *
     * @returns publish meta data properties.
     */
    const publish = async ({ isStaging, region }: Pick<ConversationPublishSlotStore, 'isStaging'> & { region: string }): Promise<void> => {
        const { appId, versionId } = context;
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps', appId)
            .resource('publish')
            .build();

        await httpService.post<void>(url, { appId, versionId, region, isStaging }, apiOptions);
    };

    /**
     * Service to get publish settings for an existing application.
     *
     * @param param0.isStaging Flag to determines if the publish app for staging or production environment.
     * @returns publish settings data properties.
     */
    const getPublishSettings = async ({
        isStaging
    }: Pick<ConversationPublishSlotStore, 'isStaging'>): Promise<ConversationPublishSlotStore> => {
        const { appId } = context;
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps', <string>appId)
            .resource('publishSettings')
            .queryParam('isStaging', `${isStaging}`)
            .build();
        const { data: publishSettings } = await httpService.get<PublishSlotMetadataSwagger>(url, apiOptions);

        return publishMetaDataSwaggerToPublishSlotMetaDataStore(publishSettings);
    };

    /**
     * Service to update the publish settings for an existing application.
     *
     * @param param0.isSentimentAnalysisOn boolean check whether the `sentiment analysis` service in on or not.
     * @param param0.isSpeechPrimingOn boolean check whether the `speech priming` service in on or not.
     */
    const updatePublishSettings = async ({
        isSentimentAnalysisOn,
        isSpeechPrimingOn
    }: Pick<ConversationPublishSlotStore, 'isSentimentAnalysisOn' | 'isSpeechPrimingOn'>): Promise<void> => {
        const { appId } = context;
        const url = urlBuilder()
            .baseUrl(apiUrl)
            .resource('apps', <string>appId)
            .resource('publishSettings')
            .build();

        const swaggerSettings = publishSlotMetaDataStoreToPublishMetaDataSwagger(appId, {
            isSentimentAnalysisOn,
            isSpeechPrimingOn
        });

        await httpService.put<void>(url, swaggerSettings, apiOptions);
    };

    return {
        publish,
        getPublishSettings,
        updatePublishSettings
    };
});
